import {
    fetchAssignedOrders, fetchAvailableBoys, fetchAwaitingOrders, fetchCancelledOrders,
    fetchCompletedOrders, fetchDataFeed,
    fetchLateOrders,
    fetchNewOrders,
    fetchPickedUpOrders,
    fetchPreparingOrders, fetchTooLateWithoutNotes, releaseHold,
} from "../service/api/orders";
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    useSortable,
} from "@dnd-kit/sortable";
import {closestCenter, DndContext} from "@dnd-kit/core";
import "../css/Dashboard.css";

import {
    CheckCircle2, XCircle, IndianRupee,
    Timer, Clock, AlertTriangle, Package,
    Store, UserCheck, BellRing, Truck, Inbox, RefreshCw, Navigation, Hourglass, Bike
} from 'lucide-react';
import TrackingMap from "../components/TrackingMap";
import NotificationToaster from "../components/NotificationToaster";
import SupportChatWidget from "../components/SupportChatWidget";

import React, {useCallback, useEffect, useState} from 'react';
import {useOrderAlerts} from "../hooks/useOrderAlerts";
import AvailablePartnersDiv from "../components/AvailablePartnersDiv";
import ArrivedTimer from "../shared/ArrivedTimer";
import ReadyAtBadge from "../shared/ReadyAtBadge";
import AnalyticsModal from "../components/modal/AnalyticsModal";
import {OffersBadge} from "../components/Orderofferspopup";
import WhatsAppSendModal from "../components/modal/WhatsappSendModal";
import TooLateNotesModal from "../components/modal/TooLateNotesModal";


const STATUS_LABEL_MAP = {
    1: { label: "New",        color: "#6366f1" },
    2: { label: "Preparing",  color: "#f59e0b" },
    3: { label: "Assigned",   color: "#3b82f6" },
    4: { label: "Picked Up",  color: "#10b981" },
    5: { label: "Completed",  color: "#059669" },
    6: { label: "Cancelled",  color: "#ef4444" },
};


const formatSlotTime = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleTimeString("en-IN", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    }

    // fallback for "17:11:00"
    const [h, m] = value.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const OrderSection = ({
                          updated,
                          onDismiss,
                          onRelease,
                          releasingIds,
                          title,
                          orders = [],
                          holdData = [],
                          slotOrderGroups = [],
                          loading,
                          setWaOrder
                      }) => {

    const slotOrdersFromOrders = (orders || []).filter((order) => {
        return order?.delivery_slot_id !== null &&
            order?.delivery_slot_id !== undefined &&
            order?.delivery_slot_id !== "";
    });


    const normalOrders = (orders || []).filter((order) => {
        return !(
            order?.delivery_slot_id !== null &&
            order?.delivery_slot_id !== undefined &&
            order?.delivery_slot_id !== ""
        );
    });

    const mergedSlotOrderGroups = (() => {
        const groupsMap = {};

        (slotOrderGroups || []).forEach((group) => {
            const slotId = String(group.delivery_slot_id);

            groupsMap[slotId] = {
                ...group,
                orders: Array.isArray(group.orders) ? [...group.orders] : [],
            };
        });

        slotOrdersFromOrders.forEach((order) => {
            const slotId = String(order.delivery_slot_id);

            if (!groupsMap[slotId]) {
                groupsMap[slotId] = {
                    delivery_slot_id: order.delivery_slot_id,
                    delivery_slot_name: order.delivery_slot_name,
                    delivery_slot_start_time: order.delivery_slot_start_time,
                    delivery_slot_end_time: order.delivery_slot_end_time,
                    orders_count: 0,
                    orders: [],
                };
            }

            const alreadyExists = groupsMap[slotId].orders.some((existingOrder) => {
                return String(existingOrder.order_id) === String(order.order_id);
            });

            if (!alreadyExists) {
                groupsMap[slotId].orders.push(order);
            }

            groupsMap[slotId].orders_count = groupsMap[slotId].orders.length;
        });

        return Object.values(groupsMap);
    })();

    const slotOrdersCount = mergedSlotOrderGroups.reduce(
        (total, group) => total + Number(group.orders_count || 0),
        0
    );

    const totalCount =
        (normalOrders?.length || 0) +
        (holdData?.length || 0) +
        slotOrdersCount;

    const isFirstLoad = loading && totalCount === 0;

    const isEmpty =
        !loading &&
        totalCount === 0;

    const skeletons = Array.from({ length: 2 });


    return (
        <section className="section-order">
            <div style={{ padding: '0px 20px' }}>
                <div className="section-header" style={{ justifyContent: "space-between" }}>
                    <p className="section-title">{title}</p>
                    <span className="order-count-badge">
                        {isFirstLoad ? "..." : totalCount}
                    </span>
                </div>
            </div>

            <div className={`orders-grid`}>
                {isFirstLoad && skeletons.map((_, i) => (
                    <div key={i} className="order-card skeleton fade-pulse">
                        <div className="skeleton-line w-60"></div>
                        <div className="skeleton-line w-40"></div>
                        <div className="skeleton-line w-50"></div>
                        <div className="skeleton-line w-70"></div>
                    </div>
                ))}

                {isEmpty && (
                    <div className="empty-state fade-in">
                        <Inbox size={30} className="empty-icon" />
                        <div>Nothing to show</div>
                    </div>
                )}

                {!isFirstLoad && normalOrders?.map((o) => {
                    const isUpdated = updated.some(u => u.id === o?.unique_order_id);
                    const isReady_For_pickup =
                        o?.live?.status === 'ready_for_pickup' &&
                        [2, 3].includes(o?.orderstatus_id);


                    const isPremium = o?.order_total >= 1000;

                    return (
                        <React.Fragment key={o?.order_id}>
                            <div
                                onMouseEnter={() => isUpdated && onDismiss(o?.unique_order_id)}
                                className={`order-card fade-in 
                                    ${isUpdated ? 'order-card--blinking' : ''} 
                                    ${isReady_For_pickup ? 'order-ready' : ''}
                                    ${isPremium ? 'order-card--premium' : ''}
                                `}
                            >
                                <div className="order-info">
                                    <div
                                        onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")}
                                        className="order-customer">
                                        <span>Customer:</span> {o?.customer_name}
                                        {o?.customer_completed_orders && (
                                            <div className="orders-count">
                                                (
                                                <div
                                                    className="order-complete-count">{o?.customer_completed_orders}</div>
                                                /
                                                <div
                                                    className="order-cancelled-count">{o?.customer_cancelled_orders}</div>)
                                            </div>
                                        )}
                                    </div>

                                    <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                        <span>Phone:</span> {o?.customer_phone}
                                    </a>
                                    <a
                                        className="order-id"
                                        href={`/order/${o?.unique_order_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span>Order:</span> #{o?.unique_order_id?.slice(10)}
                                    </a>
                                    <div className="order-store"><span>Restaurant:</span> {o?.restaurant_name}</div>
                                    <div className="order-address"><span>Address:</span> {o?.order_addresss}</div>

                                    {o?.delivery_guy_name && (
                                        <>
                                            <div
                                                onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.delivery_guy_id}`, "_blank")}
                                                className="order-phone">
                                                <span>Delivery Guy:</span> {o?.delivery_guy_name} ( <span style={{
                                                fontSize: '14px',
                                                color: "#10b981",
                                                fontWeight: '600'
                                            }}>{o?.completed_orders_today}</span> / <span style={{
                                                fontSize: '14px',
                                                color: "#e65d5d",
                                                fontWeight: '600'
                                            }}>{o?.non_complete_order_count}</span> )
                                            </div>
                                            <a href={`tel:${o?.delivery_guy_phone}`} className="order-phone">
                                                <span>Phone:</span> {o?.delivery_guy_phone}
                                            </a>
                                        </>
                                    )}

                                    <ArrivedTimer
                                        arrivedAt={o?.arrived_at_restaurant}
                                        statusId={o?.orderstatus_id}
                                    />

                                    {!isReady_For_pickup && (
                                        <ReadyAtBadge readyAt={o?.live?.ready_at}/>
                                    )}

                                    {title === "Hold Orders" && (
                                        <button
                                            className="btn-release"
                                            onClick={() => onRelease(o)}
                                            disabled={releasingIds?.has(o.order_id)}
                                        >
                                            {releasingIds?.has(o.order_id) ? (
                                                <span className="btn-spinner"/>
                                            ) : (
                                                'Release'
                                            )}
                                        </button>
                                    )}
                                </div>

                                <OffersBadge order={o}/>


                                <div className="order-footer">
                                    <div className="order-price">₹{o?.order_total}</div>
                                    <div className="payment-tag">{o?.payment_mode}</div>
                                    <div className="order-created">{o?.created_time}</div>
                                    <div className="order-updated">{o?.updated_time}</div>

                                    <button
                                        className="wa-card-btn"
                                        title="Send WhatsApp to customer"
                                        onClick={() => setWaOrder(o)}
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                            <path
                                                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="div-bar"/>
                        </React.Fragment>
                    );
                })}

                {!loading && holdData?.length > 0 && (
                    <>
                        <div style={{
                            width: '100%',
                            borderTop: '2px dashed #f59e0b',
                            margin: '12px 0 4px',
                            position: 'relative',
                        }}>
                            <span style={{
                                position: 'absolute',
                                top: '-11px',
                                left: '12px',
                                background: '#fff8ed',
                                color: '#f59e0b',
                                fontSize: '11px',
                                fontWeight: '700',
                                padding: '0 8px',
                                borderRadius: '4px',
                                letterSpacing: '0.5px',
                            }}>
                                HOLD ORDERS ({holdData.length})
                            </span>
                        </div>

                        {holdData.map((o) => {
                            const isUpdated = updated.some(u => u.id === o?.unique_order_id);
                            return (
                                <React.Fragment key={o?.order_id}>
                                    <div
                                        onMouseEnter={() => isUpdated && onDismiss(o?.unique_order_id)}
                                        className={`order-card fade-in ${isUpdated ? 'order-card--blinking' : ''}`}
                                        style={{ borderLeft: '3px solid #f59e0b' }}
                                    >
                                        <div className="order-info">
                                            <div onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")} className="order-customer">
                                                <span>Customer:</span> {o?.customer_name}
                                                {o?.customer_completed_orders && (
                                                    <div className="orders-count">
                                                        (<div className="order-complete-count">{o?.customer_completed_orders}</div>
                                                        /<div className="order-cancelled-count">{o?.customer_cancelled_orders}</div>)
                                                    </div>
                                                )}
                                            </div>
                                            <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                                <span>Phone:</span> {o?.customer_phone}
                                            </a>
                                            <a className="order-id" href={`/order/${o?.unique_order_id}`} target="_blank" rel="noopener noreferrer">
                                                <span>Order:</span> #{o?.unique_order_id?.slice(10)}
                                            </a>
                                            <div className="order-store"><span>Restaurant:</span> {o?.restaurant_name}</div>
                                            <div className="order-address"><span>Address:</span> {o?.order_addresss}</div>

                                            <button
                                                className="btn-release"
                                                onClick={() => onRelease(o)}
                                                disabled={releasingIds?.has(o.order_id)}
                                            >
                                                {releasingIds?.has(o.order_id) ? <span className="btn-spinner" /> : 'Release'}
                                            </button>
                                        </div>

                                        <div className="order-footer">
                                            <div className="order-price">₹{o?.order_total}</div>
                                            <div className="payment-tag">{o?.payment_mode}</div>
                                            <div className="order-created">{o?.created_time}</div>
                                            <div className="order-updated">{o?.updated_time}</div>
                                        </div>
                                    </div>
                                    <div className="div-bar" />
                                </React.Fragment>
                            );
                        })}
                    </>
                )}


                {!isFirstLoad && mergedSlotOrderGroups?.length > 0 && (
                    <>
                        <div className="slot-orders-separator">
                        <span>
                            DELIVERY SLOT ORDERS (
                            {slotOrdersCount}
                            )
                        </span>
                        </div>

                        {mergedSlotOrderGroups.map((group) => (
                            <div key={group.delivery_slot_id} className="slot-group-block">
                                <div className="slot-group-header">
                                    <div>
                                        <div className="slot-group-title">
                                            {group.delivery_slot_name || "Unnamed Slot"}
                                        </div>
                                        <div className="slot-group-time">
                                            {formatSlotTime(group.delivery_slot_start_time)} - {formatSlotTime(group.delivery_slot_end_time)}
                                        </div>
                                    </div>

                                    <span className="slot-group-count">
                                        {group.orders_count}
                                    </span>
                                </div>

                                {(group.orders || []).map((o) => {
                                    const isUpdated = updated.some(u => u.id === o?.unique_order_id);
                                    const isPremium = o?.order_total >= 1000;

                                    return (
                                        <React.Fragment key={o?.order_id}>
                                            <div
                                                onMouseEnter={() => isUpdated && onDismiss(o?.unique_order_id)}
                                                className={`order-card fade-in 
                                    ${isUpdated ? 'order-card--blinking' : ''} 
                                    ${isPremium ? 'order-card--premium' : ''}
                                `}
                                                style={{ borderLeft: '3px solid #8b5cf6' }}
                                            >
                                                <div className="order-info">
                                                    <div
                                                        onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")}
                                                        className="order-customer"
                                                    >
                                                        <span>Customer:</span> {o?.customer_name}
                                                        {o?.customer_completed_orders && (
                                                            <div className="orders-count">
                                                                (
                                                                <div className="order-complete-count">
                                                                    {o?.customer_completed_orders}
                                                                </div>
                                                                /
                                                                <div className="order-cancelled-count">
                                                                    {o?.customer_cancelled_orders}
                                                                </div>
                                                                )
                                                            </div>
                                                        )}
                                                    </div>

                                                    <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                                        <span>Phone:</span> {o?.customer_phone}
                                                    </a>

                                                    <a
                                                        className="order-id"
                                                        href={`/order/${o?.unique_order_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <span>Order:</span> #{o?.unique_order_id?.slice(10)}
                                                    </a>

                                                    <div className="order-store">
                                                        <span>Restaurant:</span> {o?.restaurant_name}
                                                    </div>

                                                    <div className="order-address">
                                                        <span>Address:</span> {o?.order_addresss}
                                                    </div>

                                                    <div className="slot-mini-badge">
                                                        Slot: {o?.delivery_slot_name}
                                                    </div>

                                                    {o?.delivery_guy_name && (
                                                        <>
                                                            <div
                                                                onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.delivery_guy_id}`, "_blank")}
                                                                className="order-phone">
                                                                <span>Delivery Guy:</span> {o?.delivery_guy_name} ( <span style={{
                                                                fontSize: '14px',
                                                                color: "#10b981",
                                                                fontWeight: '600'
                                                            }}>{o?.completed_orders_today}</span> / <span style={{
                                                                fontSize: '14px',
                                                                color: "#e65d5d",
                                                                fontWeight: '600'
                                                            }}>{o?.non_complete_order_count}</span> )
                                                            </div>
                                                            <a href={`tel:${o?.delivery_guy_phone}`} className="order-phone">
                                                                <span>Phone:</span> {o?.delivery_guy_phone}
                                                            </a>
                                                        </>
                                                    )}
                                                </div>

                                                <OffersBadge order={o} />

                                                <div className="order-footer">
                                                    <div className="order-price">₹{o?.order_total}</div>
                                                    <div className="payment-tag">{o?.payment_mode}</div>
                                                    <div className="order-created">{o?.created_time}</div>
                                                    <div className="order-updated">{o?.updated_time}</div>

                                                    <button
                                                        className="wa-card-btn"
                                                        title="Send WhatsApp to customer"
                                                        onClick={() => setWaOrder(o)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="div-bar" />
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        ))}
                    </>
                )}

            </div>

        </section>
    );
};

const StatusDiv = ({ label, count, loading, onClick }) => {
    const displayLabel = label?.replace(/_/g, ' ');
    const config = iconMap[displayLabel] || { icon: Package, color: "#64748b" };
    const Icon = config.icon;
    const isClickable = !!STAT_TO_TAB[displayLabel?.toLowerCase()];

    return (
        <div
            className="status-card"
            onClick={isClickable ? onClick : undefined}
            style={isClickable ? { cursor: 'pointer' } : {}}
        >
            <div
                className="status-icon-wrapper"
                style={{ backgroundColor: `${config.color}15` }}
            >
                {loading ? (
                    <div className="skeleton-circle fade-pulse" />
                ) : (
                    <Icon size={20} color={config.color} />
                )}
            </div>

            <div className="status-content">
                {loading ? (
                    <>
                        <div className="skeleton-line w-40 fade-pulse" style={{ height: 14 }} />
                        <div className="skeleton-line w-60 fade-pulse" style={{ height: 10, marginTop: 6 }} />
                    </>
                ) : (
                    <>
                        <div className="count">{count}</div>
                        <div className="label">{displayLabel?.toUpperCase()}</div>
                    </>
                )}
            </div>
        </div>
    );
};

const SortableItem = ({id, children}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id});

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        position: 'relative',
        marginTop: '10px',
        minWidth: 0,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className="drag-handle" {...listeners}>
                <div className="grip-dots">⋮⋮</div>
            </div>
            {children}
        </div>
    );
};

const iconMap = {
    "completed orders": { icon: CheckCircle2, color: "#10b981" },
    "cancelled orders": { icon: XCircle, color: "#ef4444" },
    "ongoing orders":   { icon: Truck,        color: "#3b82f6" },
    "total sales":      { icon: IndianRupee,   color: "#059669" },
    "on-time orders":   { icon: Timer,         color: "#8b5cf6" },
    "late orders":      { icon: Clock,         color: "#f59e0b" },
    "too late orders":  { icon: AlertTriangle, color: "#b91c1c" },
    "total orders":     { icon: Package,       color: "#64748b" },
    "accepted by store":{ icon: Store,         color: "#0ea5e9" },
    "accepted by admin":{ icon: UserCheck,     color: "#6366f1" },

    "delivery accepted": { icon: UserCheck, color: "#14b8a6" },
    "delivery assigned": { icon: Truck,     color: "#3b82f6" },

    "ready by store":   { icon: BellRing,      color: "#ec4899" },
    "reassigned orders": { icon: RefreshCw,     color: "#f97316" },

    "assign to arrived":  { icon: Navigation, color: "#06b6d4" },
    "wait at restaurant": { icon: Hourglass,  color: "#eab308" },
    "pickup to delivery": { icon: Bike,       color: "#22c55e" },


};

const parseCreatedTime = (text) => {
    if (!text) return 0;

    const lower = text.toLowerCase();

    // minutes
    const minMatch = lower.match(/(\d+)\s*minute/);
    if (minMatch) return parseInt(minMatch[1]);

    // hours
    const hourMatch = lower.match(/(\d+)\s*hour/);
    if (hourMatch) return parseInt(hourMatch[1]) * 60;

    return 0;
};

const STAT_TO_TAB = {
    'wait at restaurant':  'wait_at_restaurant',
    'assign to arrived':   'assign_to_arrived',
    'pickup to delivery':  'pickup_to_delivery',
    'delivery assigned':   'delivery_assigned',
    'delivery accepted':   'delivery_accepted',
    'too late orders':     'too_late_orders',
    'cancelled orders':     'cancelled_orders',
    'ready by store':      'ready_by_store',
};

const getOrderStyle = (createdTime) => {
    const minutes = parseCreatedTime(createdTime);

    if (minutes >= 45) {
        return {
            borderColor: "#ef4444",
            borderWidth: 3,
            boxShadow: "0 0 8px rgba(239,68,68,0.4)"
        };
    }

    if (minutes >= 30) {
        return {
            borderColor: "#f97316",
            borderWidth: 1,
            boxShadow: "0 0 6px rgba(249,115,22,0.3)"
        };
    }

    return {
        borderColor: "#cbd5e1",
        borderWidth: 1
    };
};


export default function Dashboard() {
    const [late, setLate] = useState([]);
    const [newOrders, setNewOrders] = useState([]);
    const [holdOrders, setHoldOrders] = useState([]);
    const [preparing, setPreparing] = useState([]);
    const [slotOrderGroups, setSlotOrderGroups] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [pickup, setPickup] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [awaiting, setAwaiting] = useState([]);
    const [cancelled, setCancelled] = useState([]);
    const [updatedOrders, setUpdatedOrders] = useState([]);
    const [cancelledByStore, setCancelledByStore] = useState([]);
    const [paymentFailed, setPaymentFailed] = useState([]);

    const [tooLateModalOpen, setTooLateModalOpen] = useState(false);
    const [tooLateOrders, setTooLateOrders] = useState([]);
    const [tooLateCount,  setTooLateCount]  = useState(0);

    const [waOrder, setWaOrder] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab]   = useState('wait_at_restaurant');

    const [sectionOrder, setSectionOrder] = useState([
        "new", "preparing", "assigned", "pickup",
        "completed", "awaiting", "cancelled", "store_cancelled", "payment_failed"
    ]);

    const [dashboard, setDashboard] = useState(null);
    const [availableBoys, setAvailableBoys] = useState([]);

    // dismissOrder removes the order from updatedOrders
    const { dismissOrder, muted, toggleMute } = useOrderAlerts(updatedOrders, setUpdatedOrders);

    const [loading, setLoading] = useState({
        late: true, new: true, preparing: true, assigned: true,
        pickup: true, completed: true, awaiting: true, cancelled: true,
        store_cancelled: true, payment_failed: true, boys: true, feed: true
    });

    const [releasingIds, setReleasingIds] = useState(new Set());

    const resetLoading = (status) => setLoading({
        late: status, new: status, hold: status, preparing: status, assigned: status,
        pickup: status, completed: status, awaiting: status, cancelled: status,
        store_cancelled: status, payment_failed: status, boys: status, feed: status,
    });

    const handleStatClick = (label) => {
        const normalized = label?.toLowerCase().replace(/_/g, ' ');
        const tab = STAT_TO_TAB[normalized];
        if (tab) {
            setModalTab(tab);
            setModalOpen(true);
        }
    };

    const loadData = useCallback(() => {
        resetLoading(true);

        const newUpdates = new Map();
        let latestNewOrders, latestPreparing, latestAssigned, latestCancelled, latestCancelledByStore, latestHoldOrders;

        Promise.allSettled([
            fetchNewOrders().then(response => {
                const allNewOrders = Array.isArray(response)
                    ? response
                    : (response?.new_orders || []);

                const slotGroups = Array.isArray(response?.slot_order_groups)
                    ? response.slot_order_groups
                    : [];

                const filteredData = (allNewOrders || []).filter(o => o.hold_status !== 'hold');
                const holdOrderData = (allNewOrders || []).filter(o => o.hold_status === 'hold');

                setNewOrders(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);

                    filteredData.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) {
                            newUpdates.set(o.unique_order_id, 'new_order');
                        }
                    });

                    latestNewOrders = filteredData;
                    return filteredData;
                });

                setHoldOrders(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);

                    holdOrderData.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) {
                            newUpdates.set(o.unique_order_id, 'new_order');
                        }
                    });

                    latestHoldOrders = holdOrderData;
                    return holdOrderData;
                });

                setSlotOrderGroups(slotGroups);
            }),

            fetchPreparingOrders().then(data => {
                setPreparing(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    data?.filter(n => n.live?.status === "ready_for_pickup").forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'ready_order');
                    });
                    latestPreparing = data;
                    return data;
                });
            }),
            fetchAssignedOrders().then(data => {
                setAssigned(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    data?.filter(n => n.live?.status === "ready_for_pickup").forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'ready_order');
                    });
                    latestAssigned = data;
                    return data;
                });
            }),
            fetchCancelledOrders().then(data => {
                const paymentFailedOrders = data?.filter(o => o?.order_type === "payment_failed");
                const cancelledByStoreOrders = data?.filter(o => o?.order_type === "store_cancelled");
                const cancelledByCustomerAndAdmin = data?.filter(o => o?.order_type === "cancelled");


                setCancelledByStore(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    cancelledByStoreOrders?.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'cancelled_by_store');
                    });
                    latestCancelledByStore = cancelledByStoreOrders;
                    return cancelledByStoreOrders;
                });

                setPaymentFailed(paymentFailedOrders);
                setCancelled(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    cancelledByCustomerAndAdmin?.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'cancelled');
                    });
                    latestCancelled = cancelledByCustomerAndAdmin;
                    return cancelledByCustomerAndAdmin;
                });
            }),

            fetchLateOrders().then(setLate),
            fetchPickedUpOrders().then(setPickup),
            fetchCompletedOrders().then(setCompleted),
            fetchAwaitingOrders().then(setAwaiting),
            fetchDataFeed().then(setDashboard),
        ]).finally(() => {
            const allCurrentOrders = new Map([
                ...(latestNewOrders?.map(o => [o.unique_order_id, 'new_order']) ?? []),
                ...(latestHoldOrders?.map(o => [o.unique_oder_id, 'new_order']) ?? []),
                ...(latestPreparing?.map(o => [o.unique_order_id, 'ready_order']) ?? []),
                ...(latestAssigned?.map(o => [o.unique_order_id, 'ready_order']) ?? []),
                ...(latestCancelled?.map(o => [o.unique_order_id, 'cancelled']) ?? []),
                ...(latestCancelledByStore?.map(o => [o.unique_order_id, 'cancelled_by_store']) ?? [])
            ]);

            setUpdatedOrders(prev => {
                const merged = new Map(newUpdates);

                prev?.forEach(({ id, status }) => {
                    if (!merged.has(id)) {
                        if (!allCurrentOrders.has(id)) return; // gone — remove
                        const currentStatus = allCurrentOrders.get(id);
                        merged.set(id, currentStatus !== status ? currentStatus : status);
                    }
                });

                return Array.from(merged, ([id, status]) => ({ id, status }));
            });

            resetLoading(false);
        });

    }, []);

    const loadAvailableBoys = useCallback(() => {
        fetchAvailableBoys().then(setAvailableBoys);
    }, []);

    useEffect(() => {
        loadData();
        loadAvailableBoys();

        const mainInterval = setInterval(loadData, 30000);
        const boysInterval = setInterval(loadAvailableBoys, 10000);

        return () => {
            clearInterval(mainInterval);
            clearInterval(boysInterval);
        };
    }, [loadData, loadAvailableBoys]);

    const fetchCount = () =>
        fetchTooLateWithoutNotes()
            .then(res => {
                const data = res?.data ?? res ?? [];
                setTooLateOrders(data);
                setTooLateCount(data.filter(o => !o.has_todo_note).length);
            })
            .catch(() => {});

    // Update the fetch useEffect:
    useEffect(() => {
        fetchCount();
        const interval = setInterval(fetchCount, 60000);
        return () => clearInterval(interval);
    }, []);

    const sectionMap = {
        new: {
            title: "New Orders",
            data: newOrders,
            holdData: holdOrders,
            slotOrderGroups: slotOrderGroups,
            isLoading: loading.new
        },
        preparing:      { title: "Preparing Orders", data: preparing,      isLoading: loading.preparing },
        assigned:       { title: "Assigned Orders",  data: assigned,       isLoading: loading.assigned },
        pickup:         { title: "Pick Up Orders",   data: pickup,         isLoading: loading.pickup },
        completed:      { title: "Completed Orders", data: completed,      isLoading: loading.completed },
        awaiting:       { title: "Awaiting Payments", data: awaiting,       isLoading: loading.awaiting },
        cancelled:      { title: "Cancelled Orders", data: cancelled,      isLoading: loading.cancelled },
        store_cancelled:{ title: "Store Cancelled",  data: cancelledByStore, isLoading: loading?.store_cancelled },
        payment_failed: { title: "Payment Failed",   data: paymentFailed,  isLoading: loading?.payment_failed },
    };

    const sections = sectionOrder.map(id => ({ id, ...sectionMap[id] }));

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setSectionOrder((prev) => {
            const oldIndex = prev.indexOf(active.id);
            const newIndex = prev.indexOf(over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
    };


    const handleRelease = async (order) => {
        setReleasingIds(prev => new Set(prev).add(order.order_id));
        try {
            const res = await releaseHold(order?.order_id);
            if (res.success) {
                setHoldOrders(prev => {
                    const released = prev.find(o => o.unique_order_id === order.unique_order_id);
                    if (released) {
                        setNewOrders(n => {
                            const alreadyExists = n.some(o => o.unique_order_id === order.unique_order_id);
                            if (alreadyExists) return n;
                            return [...n, { ...released, hold_status: null }];
                        });
                    }
                    return prev.filter(o => o.unique_order_id !== order.unique_order_id);
                });
            }
        } catch (err) {
            console.error('Release failed', err);
        } finally {
            setReleasingIds(prev => {
                const next = new Set(prev);
                next.delete(order.order_id);
                return next;
            });
        }
    };


    return (
        <div className="dashboard">

            <div className="dashboard-head"
                 style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <p className="dashboard-title">Live orders</p>

                <div style={{display: 'flex', alignItems: 'center', gap: 20}}>
                    <div style={{position: 'relative'}}>
                        <button
                            onClick={() => setTooLateModalOpen(true)}
                            title="Too late orders without reasons"
                            style={{
                                display: 'flex', alignItems: 'center',
                                background: tooLateCount > 0 ? '#fef2f2' : '#f4f5f7',
                                border: `1px solid ${tooLateCount > 0 ? '#fecaca' : '#e2e4e9'}`,
                                borderRadius: 16, padding: '7px', cursor: 'pointer',
                                transition: 'all .15s',
                                position: 'relative',
                            }}
                        >
                            <div style={{
                                width: 34, height: 34, borderRadius: 13,
                                background: tooLateCount > 0 ? '#fee2e2' : '#f1f5f9',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                position: 'relative',
                            }}>
                                <svg
                                    width="20" height="20" viewBox="0 0 24 24"
                                    fill="none"
                                    stroke={tooLateCount > 0 ? '#dc2626' : '#94a3b8'}
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"/>
                                    <polyline points="12 6 12 12 16 14"/>
                                </svg>

                                {tooLateCount > 0 && (
                                    <span style={{
                                        position: 'absolute', top: -6, right: -6,
                                        background: '#dc2626', color: '#fff',
                                        borderRadius: 10, fontSize: 10, fontWeight: 800,
                                        padding: '1px 5px', minWidth: 18, textAlign: 'center',
                                        lineHeight: '16px', height: 16,
                                        boxShadow: '0 0 0 2px #fff',
                                    }}>
                {tooLateCount}
            </span>
                                )}
                            </div>
                        </button>
                    </div>

                    <div style={{position: "relative"}}>
                        <NotificationToaster
                            updatedOrders={updatedOrders}
                            onDismissOrder={dismissOrder}
                            muted={muted}
                            onToggleMute={toggleMute}
                        />
                    </div>
                </div>
            </div>

            <section className="stats-grid">
                {(loading?.feed && (!dashboard || dashboard.length === 0))
                    ? Array.from({length: 17}).map((_, i) => (
                        <StatusDiv key={i} label="loading" count={0} loading={true}/>
                    ))
                    : Object.entries(dashboard || {}).map(([key, value]) => (
                        <StatusDiv
                            key={key}
                            label={key}
                            count={value}
                            loading={false}
                            onClick={() => handleStatClick(key)}
                        />
                    ))}
            </section>

            <section className="section">
                <div className="section-header section-header-modern">
                    <p className="section-title">Available Partners</p>
                    <span>{availableBoys?.length}</span>
                </div>

                <div className="partners-grid">
                    {(loading.boys && (!availableBoys || availableBoys.length === 0))
                        ? Array.from({length: 4}).map((_, i) => (
                            <AvailablePartnersDiv key={i} loading/>
                        ))
                        : availableBoys?.map((b, i) => (
                            <AvailablePartnersDiv
                                key={i}
                                id={b?.user_id}
                                heading={b?.detail?.heading}
                                name={b?.detail?.name}
                                photo={b?.detail?.photo}
                                phone={b?.phone || 'N/A'}
                                pending_orders={b?.non_complete_orders}
                                completed_orders={b?.completed_orders}
                                latitude={b?.detail?.delivery_lat}
                                longitude={b?.detail?.delivery_long}
                            />
                        ))
                    }
                </div>
            </section>

            <section className="section">
                <div className="section-header section-header-modern">
                    <p className="section-title">Getting Late</p>
                    <span>{late?.length}</span>
                </div>

                <div className="partners-grid">
                    {(loading.late && (!late || late.length === 0))
                        ? Array.from({length: 4}).map((_, i) => (
                            <AvailablePartnersDiv key={i} loading/>
                        ))
                        : late?.map((o) => {
                            const statusInfo = STATUS_LABEL_MAP[o?.orderstatus_id];

                            const isReady_For_pickup =
                                o?.live?.status === 'ready_for_pickup' &&
                                [2, 3].includes(o?.orderstatus_id);

                            return (
                                <React.Fragment key={o?.order_id}>
                                    <div
                                        className={`order-card fade-in ${isReady_For_pickup ? 'order-ready' : ''}`}
                                        style={getOrderStyle(o?.created_time)}
                                    >
                                        <div className="order-info">

                                            {statusInfo && (
                                                <div style={{
                                                    display: 'inline-block',
                                                    padding: '2px 10px',
                                                    borderRadius: '8px',
                                                    marginLeft: 10,
                                                    marginTop: 5,
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    alignSelf: "flex-start",
                                                    letterSpacing: '0.5px',
                                                    backgroundColor: `${statusInfo.color}20`,
                                                    color: statusInfo.color,
                                                    border: `1px solid ${statusInfo.color}50`,
                                                    marginBottom: '6px',
                                                }}>
                                                    {statusInfo.label}
                                                </div>
                                            )}

                                            <div
                                                onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")}
                                                className="order-customer"
                                            >
                                                <span>Customer:</span> {o?.customer_name}
                                                <div className="orders-count">
                                                    (
                                                    <div
                                                        className="order-complete-count">{o?.customer_completed_orders}</div>
                                                    /
                                                    <div
                                                        className="order-cancelled-count">{o?.customer_cancelled_orders}</div>
                                                    )
                                                </div>
                                            </div>

                                            <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                                <span>Phone:</span> {o?.customer_phone}
                                            </a>

                                            <a
                                                className="order-id"
                                                href={`/order/${o?.unique_order_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span>Order:</span> #{o?.unique_order_id?.slice(10)}
                                            </a>

                                            <div className="order-store">
                                                <span>Restaurant:</span> {o?.restaurant_name}
                                            </div>

                                            <div
                                                className="order-html"
                                                dangerouslySetInnerHTML={{__html: o?.delivery_details}}
                                            />

                                            {o?.orderstatus_id === 3 && (
                                                <ArrivedTimer
                                                    arrivedAt={o?.arrived_at_restaurant}
                                                    statusId={o?.orderstatus_id}
                                                />
                                            )}

                                            {(!isReady_For_pickup && [2, 3]?.includes(o?.orderstatus_id)) && (
                                                <ReadyAtBadge readyAt={o?.live?.ready_at} />
                                            )}
                                        </div>

                                        <div className="order-footer">
                                            <div className="order-price">₹{o?.order_total}</div>
                                            <div className="payment-tag">{o?.payment_mode}</div>
                                            <div className="order-created">{o?.created_time}</div>
                                            <div className="order-updated">{o?.updated_time}</div>

                                            {/* ── WhatsApp icon button ── */}
                                            <button
                                                className="wa-card-btn"
                                                title="Send WhatsApp to customer"
                                                onClick={() => setWaOrder(o)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                    <path
                                                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                </div>
            </section>

            {waOrder && (
                <WhatsAppSendModal
                    order={waOrder}
                    onClose={() => setWaOrder(null)}
                />
            )}

            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sectionOrder} strategy={rectSortingStrategy}>
                    <div className="dashboard-container">
                        {sections.map((section) => (
                            <SortableItem key={section.id} id={section.id}>
                                <OrderSection
                                    slotOrderGroups={section?.slotOrderGroups}
                                    onDismiss={dismissOrder}
                                    updated={updatedOrders}
                                    title={section.title}
                                    holdData={section.holdData ?? []}
                                    orders={section.data}
                                    loading={section.isLoading}
                                    onRelease={handleRelease}
                                    releasingIds={releasingIds}
                                    setWaOrder={setWaOrder}
                                />
                            </SortableItem>
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            <SupportChatWidget />

            <TrackingMap points={availableBoys} orderPoints={[...newOrders, ...preparing, ...assigned, ...pickup]}/>

            <AnalyticsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                initialTab={modalTab}
            />


            <TooLateNotesModal
                open={tooLateModalOpen}
                orders={tooLateOrders}
                setTooLateCount={setTooLateCount}
                onRefresh={fetchCount}
                onClose={() => setTooLateModalOpen(false)}
            />

        </div>
    );
}